import React, {useState, useEffect} from 'react';
import {Block, Select, Map, SelectGroup, Icon} from '../components';
import Table from "../tables/Table";
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from "react-country-flag";
import isEqual from "lodash/isEqual";
import isEqualWith from "lodash/isEqualWith";
import {Badge, Col, Container, Row} from "react-bootstrap";
import {useGetBoundariesQuery, useLazyGetBoundariesQuery} from '../../api/boundariesApiSlice';
import useCustomTable from '../tables/useCustomTable';


const optionEquality = (option1, option2) => {
  const filteredOption1 = option1 ? {level: option1.level, country: option1.country, value: option1.value} : null
  const filteredOptions2 = option2 ? {level: option2.level, country: option2.country, value: option2.value} : null
  return isEqual(filteredOption1, filteredOptions2)
}

function AdminBoundariesFilterForm({onChange, value, disabled, isValid, isInvalid, errors}) {
  const [country, setCountry] = useState('fr')
  const [adminLevel, setAdminLevel] = useState(null)
  const [selected, setSelected] = useState(value)
  const [tableRowHighlighted, setTableRowHighlighted] = useState(null);
  const [mapHovered, setMapHovered] = useState(null);
  const { t } = useTranslation();

  const {data: countries = []} = useGetBoundariesQuery({admin_level: 0})
  const {data: regions = []} = useGetBoundariesQuery({admin_level: 1, country: 'fr'})
  const {data: departments = []} = useGetBoundariesQuery({admin_level: 2, country: 'fr'})

  const optionsForLevel = (level) => {
    switch (level) {
      case 0:
        return countries
      case 1:
        return regions
      case 2:
        return departments
      default:
        return undefined
    }
  }


  useEffect(() => {
    setSelected(value)
  }, [value])

  useEffect(() => {
    onChange(selected)
  }, [selected])

  const flagSvgStyle = {
    fontSize: '1em',
    marginBottom: '0.2em',
    marginRight: '0.2em',
    width: '1.2em,'
  }

  const optionValueToSelected = (value) => ({
    level: value.level,
    country: value.level > 0 ? country : null,
    name: value.name,
    value: value.value
  })

  const handleChange = (value) => {
    const newSelected = optionValueToSelected(value)
    if (!isAlreadySelected(newSelected)) {
      setSelected(prevSelected => [newSelected, ...prevSelected])
    }
  }

  const isOptionDisabled = (option) => {
    return option && option.value && isAlreadySelected(optionValueToSelected(option.value))
  }

  const selectProps = {
    style: {minWidth: "150px", flex: 1},
    isDisabled: adminLevel === null || adminLevel > 1 && !country,
    onChange: handleChange,
    isOptionDisabled: isOptionDisabled
  }

  const CountriesSelect = (props) => {
    return <Select {...props} {...selectProps} >
      {countries.map( (c) => <Option key={c.alpha2} value={{value: c.alpha2, name: t(`countries.${c.alpha2}`), level: 0}}><ReactCountryFlag countryCode={c.alpha2} svg style={flagSvgStyle}/> {t(`countries.${c.alpha2}`)}</Option>)}
    </Select>
  }

  const RegionsSelect = (props) => {
    return <Select {...props} {...selectProps}>
      {regions.map( (region) => <Option key={region.code} value={{value: region.code.toString(), name: region.name, level: 1}}>{region.name}</Option>)}
    </Select>
  }

  const DepartmentsSelect = (props) => {
    return <Select {...props} {...selectProps}>
      {departments.map( (department) => <Option key={department.code} value={{value: department.code.toString(), name: department.name, level: 2}}>{department.code} - {department.name}</Option>)}
    </Select>
  }

  const formatOptionObject = (level) => {
    return (obj) => {
      if (typeof (obj) === 'object') {
        return {
          value: {
            value: obj.code,
            name: obj.name,
            level: level
          },
          label: `${obj.code} - ${obj.name}`
        }
      } else {
        return {
          value: null,
          label: obj
        }
      }
    }
  }

  const AdminLevelSelect = (props) => {
    const [trigger] = useLazyGetBoundariesQuery()
    const {level, ...filteredProps} = props
    const loadOptions = ( inputValue, callback ) => {
      trigger({admin_level: props.level, country: 'fr', search: inputValue}, true).then((response) => {
        const options = response.data.map(formatOptionObject(level))
        callback(options)
      })
    }
    return <Select
      {...filteredProps}
      {...selectProps}
      loadOptions={loadOptions}
      formatOption={formatOptionObject(level)}
    >
      <Option>Please enter something</Option>
    </Select>
  }

  const columns = [{
      Header: 'Name',
      accessor: 'name',
      Cell: (cell, row) => {
        if (cell.row.original.level == 0) {
          return String(t("countries."+cell.row.original.value))
        } else {
          return <div>
            {String(cell.value)}<br/>
            <b>{String(t("countries."+cell.row.original.country))}</b>
          </div>
        }
      }
    },{
      Header: 'Level',
      accessor: 'level',
      Cell: (cell) => <Badge pill variant="primary">{`level ${cell.value}`}</Badge>
    },{
      Header: '',
      accessor: 'id',
      Cell: (cell) => <div onClick={() => removeItem(cell.row.index)}><Icon icon="close-circle" variant="danger" size="lg"/></div>
    }
  ]

  const removeItem = (index) => {
    setSelected(prevSelected => {
      let newSelected = prevSelected.slice()
      newSelected.splice(index, 1)
      return newSelected
    })
  }

  const tableOptions = {
    sortable: true,
    filterable: false,
    paginate: true,
    header: false
  }

  const { tableProps } = useCustomTable()

  function Placeholder() {
    return <Container style={{height: '100%'}}>
      <Row className="justify-content-center align-items-center" style={{height: "100%"}}>
        Please select something
      </Row>
    </Container>
  }

  const handleRowHover = (row) => setMapHovered(row ? {value: row.value, level: row.level} : null)

  const handleMapHover = (feature) => {
    if (feature) {
      const index = selected.findIndex(x => x.value == feature.value && x.level == feature.level)
      setTableRowHighlighted(index >= 0 ? index : null)
    }
  }

  const isAlreadySelected = (option) => {
    return selected.some(x => isEqualWith(x, option, optionEquality))
  }

  const addMissingNamesFor = (data) => {
    data.forEach(x => {
      if (!x.name) {
        if (x.level == 1 || x.level == 2) {
          const levelOptions = optionsForLevel(x.level)
          x.name = levelOptions && levelOptions.find(el => el.code == x.value).name
        } else if (x.level == 3 || x.level == 4) {
          // asynchronous API call, not sure how to do it
        }
      }
    })
    return data
  }

  const heightStyle = {
    height: '500px',
    marginBottom: '30px'
  }

  return (
    <Row>
      <Block as={Col} md={12} lg={6} fullHeight style={heightStyle}>
        {!disabled && <SelectGroup style={{marginBottom: "20px"}}>
          <Select
              style={{minWidth: "150px"}}
              placeholder="Admin level"
              onChange={(val) => setAdminLevel(val)}
              value={adminLevel}
              isDisabled={disabled}>
            {[0, 1, 2, 3, 4].map( (level) => <Option key={level} value={level}>{`${level} - ${t(`adminBoundaries.level${level}`)}`}</Option>)}
          </Select>
          {adminLevel > 0 && <Select
            style={{minWidth: "150px"}}
            placeholder="choose a country"
            onChange={(val) => setCountry(val)}
            value={country}
            isOptionDisabled={(option) => option.value !== 'fr'}
          >
            {countries.map( (c) => <Option key={c.alpha2} value={c.alpha2}><ReactCountryFlag countryCode={c.alpha2} svg style={flagSvgStyle}/> {t(`countries.${c.alpha2}`)}</Option>)}
          </Select>}
          {adminLevel === null && <Select {...selectProps} />}
          {adminLevel === 0 && <CountriesSelect/>}
          {adminLevel === 1 && <RegionsSelect/>}
          {adminLevel === 2 && <DepartmentsSelect/>}
          {adminLevel === 3 && <AdminLevelSelect level={3}/>}
          {adminLevel === 4 && <AdminLevelSelect level={4}/>}
        </SelectGroup>}
        {selected && selected.length > 0 ? <Table
          data={selected}
          columns={columns}
          {...tableProps}
          {...tableOptions}
          highlighted={tableRowHighlighted}
          onRowHover={handleRowHover}
          // onRowClick={showCoordinates}
          hiddenColumns={disabled ? ['id'] : []}
        /> : <Placeholder/>}
      </Block>
      <Col md={12} lg={6} style={heightStyle}>
        <Map
          interactiveLayer={['countries', 'regions', 'departments', 'cities', 'iris'][adminLevel || 0]}
          selected={selected}
          hovered={mapHovered}
          onHover={handleMapHover}
          onChange={data => {
            setSelected(addMissingNamesFor(data))
          }}
          disabled={disabled}
        />
      </Col>
    </Row>
  )
}

export default AdminBoundariesFilterForm

AdminBoundariesFilterForm.propTypes = {

}
